import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { APP_THEME } from "../theme/theme";
import { apiCall } from "../utils/api";
import toast, { Toaster } from "react-hot-toast";

const KycVerification = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [shadow, setShadow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [kycCompleted, setKycCompleted] = useState(false);

    // Step 1: Email Verification
    const [email, setEmail] = useState("");
    const [emailOtp, setEmailOtp] = useState("");
    const [showEmailOtp, setShowEmailOtp] = useState(false);
    const [emailVerified, setEmailVerified] = useState(false);
    const [emailOtpSent, setEmailOtpSent] = useState(false);
    const [emailTimer, setEmailTimer] = useState(0);
    const [emailOTPToken, setEmailOTPToken] = useState('');

    // Step 2: Aadhaar Verification
    const [aadhaarNumber, setAadhaarNumber] = useState("");
    const [aadhaarName, setAadhaarName] = useState("");
    const [aadhaarOtp, setAadhaarOtp] = useState("");
    const [showAadhaarOtp, setShowAadhaarOtp] = useState(false);
    const [aadhaarVerified, setAadhaarVerified] = useState(false);
    const [aadhaarOtpSent, setAadhaarOtpSent] = useState(false);
    const [aadhaarTimer, setAadhaarTimer] = useState(0);
    const [aadhaarToken, setAadhaarToken] = useState('');

    // Step 3: PAN Verification
    const [panName, setPanName] = useState("");
    const [panNumber, setPanNumber] = useState("");
    const [panVerified, setPanVerified] = useState(false);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const result = await apiCall(
                    "profile-fetch",
                    {},
                    true
                );

                if (result.status) {
                    if (result.profile.kyc_verification === "1") {
                        setCurrentStep(3);
                        return;
                    }
                    if (result.profile.email_verification === "1") {
                        setCurrentStep(2);
                        return;
                    }
                } else {
                    toast.error(result.message || "An error occurred. Please try again.");
                }
            } catch (error) {
                toast.error("Network error. Please try again.");
            } finally {
                setFetching(false);
            }
        }
        fetchProfile();
    }, [])

    useEffect(() => {
        const handleScroll = () => {
            setShadow(window.scrollY > 10);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Timer for OTP resend
    useEffect(() => {
        let interval;
        if (emailTimer > 0) {
            interval = setInterval(() => {
                setEmailTimer((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [emailTimer]);

    useEffect(() => {
        let interval;
        if (aadhaarTimer > 0) {
            interval = setInterval(() => {
                setAadhaarTimer((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [aadhaarTimer]);

    // Step 1: Send Email OTP
    const handleSendEmailOtp = async () => {
        if (!email) {
            toast.error("Please enter your email address");
            return;
        }

        if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
            toast.error("Please enter a valid email address");
            return;
        }

        setLoading(true);
        try {
            const result = await apiCall(
                "send-email-otp",
                { email },
                true
            );

            if (result.status === true) {
                setShowEmailOtp(true);
                setEmailOtpSent(true);
                setEmailTimer(60);
                setEmailOTPToken(result.otp_token);
                toast.success("OTP sent to your email");
            } else {
                toast.error(result.message || "Failed to send OTP");
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Step 1: Verify Email OTP
    const handleVerifyEmailOtp = async () => {
        if (!emailOtp || emailOtp.length < 4) {
            toast.error("Please enter the OTP");
            return;
        }

        setLoading(true);
        try {
            const result = await apiCall(
                "verify-email-otp",
                {
                    email, otp: emailOtp,
                    email_otp_token: emailOTPToken
                },
                true
            );

            if (result.status === true) {
                setEmailVerified(true);
                toast.success("Email verified successfully!");
                setTimeout(() => {
                    setCurrentStep(2);
                }, 1000);
            } else {
                toast.error(result.message || "Invalid OTP. Please try again.");
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Step 2: Send Aadhaar OTP
    const handleSendAadhaarOtp = async () => {
        if (!aadhaarName.trim()) {
            toast.error("Please enter your name as per Aadhaar");
            return;
        }

        if (!aadhaarNumber || aadhaarNumber.length < 12) {
            toast.error("Please enter a valid 12-digit Aadhaar number");
            return;
        }

        if (!aadhaarNumber.match(/^[0-9]{12}$/)) {
            toast.error("Please enter a valid 12-digit Aadhaar number");
            return;
        }

        setLoading(true);
        try {
            const result = await apiCall(
                "send-aadhaar-otp",
                { name: aadhaarName, aadhaar: aadhaarNumber },
                true
            );

            if (result.status === true) {
                setShowAadhaarOtp(true);
                setAadhaarOtpSent(true);
                setAadhaarTimer(60);
                setAadhaarToken(result.reference_id);
                toast.success("OTP sent to your registered mobile");
            } else {
                toast.error(result.message || "Failed to send OTP");
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Step 2: Verify Aadhaar OTP
    const handleVerifyAadhaarOtp = async () => {
        if (!aadhaarOtp || aadhaarOtp.length < 4) {
            toast.error("Please enter the OTP");
            return;
        }

        setLoading(true);
        try {
            const result = await apiCall(
                "verify-aadhaar-otp",
                {
                    aadhaar: aadhaarNumber,
                    otp: aadhaarOtp,
                    aadhaar_token: aadhaarToken
                },
                true
            );

            if (result.status === true) {
                setAadhaarVerified(true);
                toast.success("Aadhaar verified successfully!");
                setTimeout(() => {
                    setCurrentStep(3);
                }, 1000);
            } else {
                toast.error(result.message || "Invalid OTP. Please try again.");
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Step 3: Verify PAN & Complete KYC
    const handleVerifyPanAndComplete = async () => {
        if (!panName.trim()) {
            toast.error("Please enter your name as per PAN");
            return;
        }

        if (!panNumber || panNumber.length < 10) {
            toast.error("Please enter a valid PAN number");
            return;
        }

        if (!panNumber.match(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/)) {
            toast.error("Please enter a valid PAN format (e.g., ABCDE1234F)");
            return;
        }

        setLoading(true);
        try {
            // Verify PAN
            const panResult = await apiCall(
                "verify-pan",
                { name: panName, pan: panNumber },
                true
            );

            if (panResult.status === true) {
                setPanVerified(true);
                toast.success("PAN verified successfully!");

                // Complete KYC
                const completeResult = await apiCall(
                    "complete-kyc",
                    {
                        email,
                        pan_name: panName,
                        pan_number: panNumber,
                        aadhaar_name: aadhaarName,
                        aadhaar_number: aadhaarNumber
                    },
                    true
                );

                if (completeResult.status === true) {
                    setKycCompleted(true);
                    toast.success("KYC completed successfully!");
                    setTimeout(() => {
                        navigate("/profile");
                    }, 1500);
                } else {
                    toast.error(completeResult.message || "Failed to complete KYC");
                }
            } else {
                toast.error(panResult.message || "PAN verification failed. Please check your details.");
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Render step indicator
    const renderStepIndicator = () => {
        return (
            <div className="flex items-center justify-center gap-2 mb-8 px-4">
                {[1, 2, 3].map((step) => (
                    <React.Fragment key={step}>
                        <div className="flex flex-col items-center">
                            <div
                                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 ${currentStep >= step
                                    ? "text-white"
                                    : "border-2"
                                    }`}
                                style={{
                                    background: currentStep >= step
                                        ? APP_THEME.primaryColor
                                        : "transparent",
                                    borderColor: currentStep > step
                                        ? APP_THEME.primaryColor
                                        : APP_THEME.borderColor,
                                    color: currentStep >= step
                                        ? "#FFFFFF"
                                        : APP_THEME.textSecondary,
                                }}
                            >
                                {currentStep > step ? "✓" : step}
                            </div>
                            <span
                                className="text-xs mt-1 font-medium"
                                style={{
                                    color: currentStep >= step
                                        ? APP_THEME.primaryColor
                                        : APP_THEME.textSecondary,
                                }}
                            >
                                {step === 1 && "Email"}
                                {step === 2 && "Aadhaar"}
                                {step === 3 && "PAN"}
                            </span>
                        </div>
                        {step < 3 && (
                            <div
                                className="flex-1 h-0.5 max-w-[60px] transition-all duration-300"
                                style={{
                                    background: currentStep > step
                                        ? APP_THEME.primaryColor
                                        : APP_THEME.borderColor,
                                }}
                            />
                        )}
                    </React.Fragment>
                ))}
            </div>
        );
    };

    // Step 1: Email Verification
    const renderEmailStep = () => {
        return (
            <div className="space-y-4">
                <div className="text-center mb-6">
                    <h2
                        className="text-xl font-bold mb-2"
                        style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}
                    >
                        Verify Your Email
                    </h2>
                    <p
                        className="text-sm"
                        style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}
                    >
                        We'll send a verification code to your email
                    </p>
                </div>

                <div className="space-y-3">
                    <div>
                        <label
                            className="text-sm font-medium mb-1.5 block"
                            style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}
                        >
                            Email Address
                        </label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter your email"
                            disabled={emailVerified || emailOtpSent}
                            className="w-full px-4 py-2.5 rounded-lg text-sm outline-none transition-all duration-200"
                            style={{
                                background: APP_THEME.surfaceColor,
                                color: APP_THEME.textPrimary,
                                border: `1px solid ${APP_THEME.borderColor}`,
                                fontFamily: APP_THEME.fontFamily,
                                opacity: emailVerified || emailOtpSent ? 0.6 : 1,
                                cursor: emailVerified || emailOtpSent ? "not-allowed" : "text",
                            }}
                        />
                        <button
                            onClick={handleSendEmailOtp}
                            disabled={loading || emailVerified || emailOtpSent}
                            className="px-4 py-2.5 w-full mt-4 rounded-lg text-sm font-semibold transition-all duration-200 active:scale-98 whitespace-nowrap"
                            style={{
                                background: emailVerified || emailOtpSent
                                    ? APP_THEME.successColor
                                    : APP_THEME.primaryColor,
                                color: APP_THEME.textOnPrimary,
                                borderRadius: APP_THEME.borderRadiusBtn,
                                fontFamily: APP_THEME.fontFamily,
                                opacity: loading || emailVerified || emailOtpSent ? 0.6 : 1,
                                cursor: loading || emailVerified || emailOtpSent ? "not-allowed" : "pointer",
                            }}
                        >
                            {loading ? "Sending..." : emailVerified ? "✓ Verified" : "Send OTP"}
                        </button>
                        {emailTimer > 0 && (
                            <p
                                className="text-xs mt-1.5"
                                style={{ color: APP_THEME.textSecondary }}
                            >
                                Resend OTP in {emailTimer}s
                            </p>
                        )}
                    </div>

                    {showEmailOtp && !emailVerified && (
                        <div className="animate-slideDown">
                            <label
                                className="text-sm font-medium mb-1.5 block"
                                style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}
                            >
                                Enter OTP
                            </label>
                            <input
                                type="text"
                                value={emailOtp}
                                onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, ""))}
                                placeholder="Enter 4-6 digit OTP"
                                className="w-full px-4 py-2.5 rounded-lg text-sm outline-none transition-all duration-200"
                                style={{
                                    background: APP_THEME.surfaceColor,
                                    color: APP_THEME.textPrimary,
                                    border: `1px solid ${APP_THEME.borderColor}`,
                                    fontFamily: APP_THEME.fontFamily,
                                }}
                            />
                            <button
                                onClick={handleVerifyEmailOtp}
                                disabled={loading}
                                className="px-4 w-full mt-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 active:scale-98"
                                style={{
                                    background: APP_THEME.primaryColor,
                                    color: APP_THEME.textOnPrimary,
                                    borderRadius: APP_THEME.borderRadiusBtn,
                                    fontFamily: APP_THEME.fontFamily,
                                    opacity: loading ? 0.6 : 1,
                                    cursor: loading ? "not-allowed" : "pointer",
                                }}
                            >
                                {loading ? "Verifying..." : "Verify"}
                            </button>
                        </div>
                    )}

                    {emailVerified && (
                        <div
                            className="p-3 rounded-lg text-sm text-center"
                            style={{
                                background: `${APP_THEME.successColor}15`,
                                color: APP_THEME.successColor,
                                border: `1px solid ${APP_THEME.successColor}30`,
                            }}
                        >
                            ✓ Email verified successfully!
                        </div>
                    )}
                </div>

                {emailVerified && (
                    <button
                        onClick={() => setCurrentStep(2)}
                        className="w-full py-3 rounded-lg text-sm font-semibold transition-all duration-200 active:scale-98 mt-4"
                        style={{
                            background: APP_THEME.primaryColor,
                            color: APP_THEME.textOnPrimary,
                            borderRadius: APP_THEME.borderRadiusBtn,
                            fontFamily: APP_THEME.fontFamily,
                        }}
                    >
                        Continue to Aadhaar →
                    </button>
                )}
            </div>
        );
    };

    // Step 2: Aadhaar Verification
    const renderAadhaarStep = () => {
        return (
            <div className="space-y-4">
                <div className="text-center mb-6">
                    <h2
                        className="text-xl font-bold mb-2"
                        style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}
                    >
                        Verify Aadhaar
                    </h2>
                    <p
                        className="text-sm"
                        style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}
                    >
                        Enter your Aadhaar details for verification
                    </p>
                </div>

                <div className="space-y-3">
                    <div>
                        <label
                            className="text-sm font-medium mb-1.5 block"
                            style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}
                        >
                            Name as per Aadhaar
                        </label>
                        <input
                            type="text"
                            value={aadhaarName}
                            onChange={(e) => setAadhaarName(e.target.value)}
                            placeholder="Enter your full name as per Aadhaar"
                            disabled={aadhaarVerified}
                            className="w-full px-4 py-2.5 rounded-lg text-sm outline-none transition-all duration-200"
                            style={{
                                background: APP_THEME.surfaceColor,
                                color: APP_THEME.textPrimary,
                                border: `1px solid ${APP_THEME.borderColor}`,
                                fontFamily: APP_THEME.fontFamily,
                                opacity: aadhaarVerified ? 0.6 : 1,
                                cursor: aadhaarVerified ? "not-allowed" : "text",
                            }}
                        />
                    </div>

                    <div>
                        <label
                            className="text-sm font-medium mb-1.5 block"
                            style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}
                        >
                            Aadhaar Number
                        </label>
                        <input
                            type="text"
                            value={aadhaarNumber}
                            onChange={(e) => setAadhaarNumber(e.target.value.replace(/\D/g, ""))}
                            placeholder="Enter 12-digit Aadhaar number"
                            disabled={aadhaarVerified || aadhaarOtpSent}
                            className="w-full px-4 py-2.5 rounded-lg text-sm outline-none transition-all duration-200"
                            style={{
                                background: APP_THEME.surfaceColor,
                                color: APP_THEME.textPrimary,
                                border: `1px solid ${APP_THEME.borderColor}`,
                                fontFamily: APP_THEME.fontFamily,
                                opacity: (aadhaarVerified || aadhaarOtpSent) ? 0.6 : 1,
                                cursor: (aadhaarVerified || aadhaarOtpSent) ? "not-allowed" : "text",
                            }}
                            maxLength={12}
                        />
                        <button
                            onClick={handleSendAadhaarOtp}
                            disabled={loading || aadhaarVerified || aadhaarOtpSent}
                            className="px-4 w-full mt-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 active:scale-98 whitespace-nowrap"
                            style={{
                                background: (aadhaarVerified || aadhaarOtpSent)
                                    ? APP_THEME.successColor
                                    : APP_THEME.primaryColor,
                                color: APP_THEME.textOnPrimary,
                                borderRadius: APP_THEME.borderRadiusBtn,
                                fontFamily: APP_THEME.fontFamily,
                                opacity: loading || aadhaarVerified || aadhaarOtpSent ? 0.6 : 1,
                                cursor: loading || aadhaarVerified || aadhaarOtpSent ? "not-allowed" : "pointer",
                            }}
                        >
                            {loading ? "Sending..." : aadhaarVerified ? "✓ Verified" : "Send OTP"}
                        </button>
                        {aadhaarTimer > 0 && (
                            <p
                                className="text-xs mt-1.5"
                                style={{ color: APP_THEME.textSecondary }}
                            >
                                Resend OTP in {aadhaarTimer}s
                            </p>
                        )}
                    </div>

                    {showAadhaarOtp && !aadhaarVerified && (
                        <div className="animate-slideDown">
                            <label
                                className="text-sm font-medium mb-1.5 block"
                                style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}
                            >
                                Enter OTP
                            </label>
                            <input
                                type="text"
                                value={aadhaarOtp}
                                onChange={(e) => setAadhaarOtp(e.target.value.replace(/\D/g, ""))}
                                placeholder="Enter 4-6 digit OTP"
                                className="w-full px-4 py-2.5 rounded-lg text-sm outline-none transition-all duration-200"
                                style={{
                                    background: APP_THEME.surfaceColor,
                                    color: APP_THEME.textPrimary,
                                    border: `1px solid ${APP_THEME.borderColor}`,
                                    fontFamily: APP_THEME.fontFamily,
                                }}
                            />
                            <button
                                onClick={handleVerifyAadhaarOtp}
                                disabled={loading}
                                className="px-4 w-full mt-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 active:scale-98"
                                style={{
                                    background: APP_THEME.primaryColor,
                                    color: APP_THEME.textOnPrimary,
                                    borderRadius: APP_THEME.borderRadiusBtn,
                                    fontFamily: APP_THEME.fontFamily,
                                    opacity: loading ? 0.6 : 1,
                                    cursor: loading ? "not-allowed" : "pointer",
                                }}
                            >
                                {loading ? "Verifying..." : "Verify"}
                            </button>
                        </div>
                    )}

                    {aadhaarVerified && (
                        <div
                            className="p-3 rounded-lg text-sm text-center"
                            style={{
                                background: `${APP_THEME.successColor}15`,
                                color: APP_THEME.successColor,
                                border: `1px solid ${APP_THEME.successColor}30`,
                            }}
                        >
                            ✓ Aadhaar verified successfully!
                        </div>
                    )}
                </div>

                {aadhaarVerified && (
                    <button
                        onClick={() => setCurrentStep(3)}
                        className="w-full py-3 rounded-lg text-sm font-semibold transition-all duration-200 active:scale-98 mt-4"
                        style={{
                            background: APP_THEME.primaryColor,
                            color: APP_THEME.textOnPrimary,
                            borderRadius: APP_THEME.borderRadiusBtn,
                            fontFamily: APP_THEME.fontFamily,
                        }}
                    >
                        Continue to PAN →
                    </button>
                )}
            </div>
        );
    };

    // Step 3: PAN Verification & Complete KYC
    const renderPanStep = () => {
        return (
            <div className="space-y-4">
                <div className="text-center mb-6">
                    <h2
                        className="text-xl font-bold mb-2"
                        style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}
                    >
                        Verify PAN & Complete KYC
                    </h2>
                    <p
                        className="text-sm"
                        style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}
                    >
                        Enter your PAN details to complete the verification
                    </p>
                </div>

                {kycCompleted ? (
                    <div
                        className="p-4 rounded-lg text-center"
                        style={{
                            background: `${APP_THEME.successColor}15`,
                            border: `1px solid ${APP_THEME.successColor}30`,
                        }}
                    >
                        <div className="text-3xl mb-2">✅</div>
                        <h3 className="text-lg font-bold mb-1" style={{ color: APP_THEME.successColor }}>
                            KYC Completed Successfully!
                        </h3>
                        <p className="text-sm" style={{ color: APP_THEME.textSecondary }}>
                            Redirecting to profile...
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="space-y-3">
                            <div>
                                <label
                                    className="text-sm font-medium mb-1.5 block"
                                    style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}
                                >
                                    Name as per PAN
                                </label>
                                <input
                                    type="text"
                                    value={panName}
                                    onChange={(e) => setPanName(e.target.value)}
                                    placeholder="Enter your full name as per PAN"
                                    disabled={panVerified}
                                    className="w-full px-4 py-2.5 rounded-lg text-sm outline-none transition-all duration-200"
                                    style={{
                                        background: APP_THEME.surfaceColor,
                                        color: APP_THEME.textPrimary,
                                        border: `1px solid ${APP_THEME.borderColor}`,
                                        fontFamily: APP_THEME.fontFamily,
                                        opacity: panVerified ? 0.6 : 1,
                                        cursor: panVerified ? "not-allowed" : "text",
                                    }}
                                />
                            </div>

                            <div>
                                <label
                                    className="text-sm font-medium mb-1.5 block"
                                    style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}
                                >
                                    PAN Number
                                </label>
                                <input
                                    type="text"
                                    value={panNumber}
                                    onChange={(e) => setPanNumber(e.target.value.toUpperCase())}
                                    placeholder="e.g., ABCDE1234F"
                                    disabled={panVerified}
                                    className="w-full px-4 py-2.5 rounded-lg text-sm outline-none transition-all duration-200 uppercase"
                                    style={{
                                        background: APP_THEME.surfaceColor,
                                        color: APP_THEME.textPrimary,
                                        border: `1px solid ${APP_THEME.borderColor}`,
                                        fontFamily: APP_THEME.fontFamily,
                                        opacity: panVerified ? 0.6 : 1,
                                        cursor: panVerified ? "not-allowed" : "text",
                                    }}
                                />
                                <p
                                    className="text-xs mt-1.5"
                                    style={{ color: APP_THEME.textSecondary }}
                                >
                                    Format: 5 letters, 4 digits, 1 letter (e.g., ABCDE1234F)
                                </p>
                            </div>

                            {panVerified && (
                                <div
                                    className="p-3 rounded-lg text-sm text-center"
                                    style={{
                                        background: `${APP_THEME.successColor}15`,
                                        color: APP_THEME.successColor,
                                        border: `1px solid ${APP_THEME.successColor}30`,
                                    }}
                                >
                                    ✓ PAN verified successfully!
                                </div>
                            )}
                        </div>

                        {!panVerified ? (
                            <button
                                onClick={handleVerifyPanAndComplete}
                                disabled={loading}
                                className="w-full py-3 rounded-lg text-sm font-semibold transition-all duration-200 active:scale-98 mt-4"
                                style={{
                                    background: APP_THEME.primaryColor,
                                    color: APP_THEME.textOnPrimary,
                                    borderRadius: APP_THEME.borderRadiusBtn,
                                    fontFamily: APP_THEME.fontFamily,
                                    opacity: loading ? 0.6 : 1,
                                    cursor: loading ? "not-allowed" : "pointer",
                                }}
                            >
                                {loading ? "Verifying & Completing..." : "Verify PAN & Complete KYC"}
                            </button>
                        ) : (
                            <div
                                className="p-3 rounded-lg text-sm text-center mt-4"
                                style={{
                                    background: `${APP_THEME.successColor}15`,
                                    color: APP_THEME.successColor,
                                    border: `1px solid ${APP_THEME.successColor}30`,
                                }}
                            >
                                ✓ All KYC details verified successfully!
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    };

    if (fetching) {
        return (
            <>
                <header
                    className="sticky top-0 left-0 w-full right-0 h-14 flex items-center justify-between px-4 z-[950] max-w-full mx-auto transition-shadow duration-200"
                    style={{
                        background: APP_THEME.bgColor,
                        borderBottom: `1px solid ${APP_THEME.borderColor}`,
                    }}
                >
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate(-1)}
                            className="bg-none border-none cursor-pointer p-1 flex items-center"
                        >
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.textPrimary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="19" y1="12" x2="5" y2="12" />
                                <polyline points="12 19 5 12 12 5" />
                            </svg>
                        </button>
                        <span
                            className="text-base font-semibold"
                            style={{
                                color: APP_THEME.textPrimary,
                                fontFamily: APP_THEME.fontFamily,
                            }}
                        >
                            KYC Verification
                        </span>
                    </div>
                    <div className="w-[22px]" />
                </header>
                <div className="text-center py-12">
                    <div className="w-10 h-10 rounded-full animate-spin mx-auto mb-4" style={{ border: `3px solid ${APP_THEME.primaryColor}33`, borderTopColor: APP_THEME.primaryColor }} />
                    <div className="text-sm font-medium" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>
                        Loading profile...
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <header
                className="sticky top-0 left-0 w-full right-0 h-14 flex items-center justify-between px-4 z-[950] max-w-full mx-auto transition-shadow duration-200"
                style={{
                    background: APP_THEME.bgColor,
                    borderBottom: `1px solid ${APP_THEME.borderColor}`,
                    boxShadow: shadow ? "0 2px 8px rgba(0,0,0,0.08)" : "none",
                }}
            >
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="bg-none border-none cursor-pointer p-1 flex items-center"
                    >
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.textPrimary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="19" y1="12" x2="5" y2="12" />
                            <polyline points="12 19 5 12 12 5" />
                        </svg>
                    </button>
                    <span
                        className="text-base font-semibold"
                        style={{
                            color: APP_THEME.textPrimary,
                            fontFamily: APP_THEME.fontFamily,
                        }}
                    >
                        KYC Verification
                    </span>
                </div>
                <div className="w-[22px]" />
            </header>

            <div className="flex flex-col min-h-[calc(100vh-57px)] py-6">
                {renderStepIndicator()}

                <div
                    className="flex-1 px-4 pb-20"
                    style={{
                        background: APP_THEME.bgColor,
                    }}
                >
                    <div
                        className="p-6 rounded-2xl transition-all duration-300"
                        style={{
                            background: APP_THEME.surfaceColor,
                            borderRadius: APP_THEME.borderRadiusCard,
                            boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                        }}
                    >
                        {currentStep === 1 && renderEmailStep()}
                        {currentStep === 2 && renderAadhaarStep()}
                        {currentStep === 3 && renderPanStep()}
                    </div>

                    {/* Progress text */}
                    <div className="text-center mt-6">
                        <p
                            className="text-xs"
                            style={{ color: APP_THEME.textSecondary }}
                        >
                            Step {currentStep} of 3
                        </p>
                    </div>
                </div>
            </div>

            <Toaster position="bottom-center" />
            <style>
                {`
                    .active\\:scale-98:active {
                        transform: scale(0.98);
                    }
                    @keyframes slideDown {
                        from {
                            opacity: 0;
                            transform: translateY(-10px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                    .animate-slideDown {
                        animation: slideDown 0.3s ease forwards;
                    }
                `}
            </style>
        </>
    );
};

export default KycVerification;